import { createElement, useState } from "react";
import { Link, router } from "@inertiajs/react";
import { AdminLayout } from "../../../layouts/admin-layout";

interface FleetDeploy {
  id: number;
  reason: string;
  status: string;
  total_count: number;
  failed_count: number;
  triggered_by_email: string | null;
  parent_id: number | null;
  created_at: string | null;
  url: string;
}

interface Pagination {
  current_page: number;
  total_pages: number;
  total_count: number;
  prev_page: number | null;
  next_page: number | null;
}

interface Preview {
  reason: string;
  count: number;
  sites: { website_id: number; url: string | null }[];
}

interface Props {
  fleet_deploys: FleetDeploy[];
  pagination: Pagination;
  can_trigger: boolean;
  preview?: Preview | null;
}

const STATUS_CLASSES: Record<string, string> = {
  completed: "bg-green-100 text-green-800",
  partial: "bg-amber-100 text-amber-800",
  failed: "bg-red-100 text-red-800",
  running: "bg-blue-100 text-blue-800",
  empty: "bg-gray-100 text-gray-600",
};

function StatusBadge({ status }: { status: string }) {
  const cls = STATUS_CLASSES[status] || "bg-gray-100 text-gray-700";
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {status}
    </span>
  );
}

export default function FleetDeploysIndex({ fleet_deploys, pagination, can_trigger, preview }: Props) {
  const [reason, setReason] = useState(preview?.reason ?? "");

  const showPreview = () => {
    if (!reason.trim()) return;
    router.get("/admin/fleet_deploys/preview", { reason });
  };

  const confirmDeploy = () => {
    router.post("/admin/fleet_deploys", { reason: preview?.reason });
  };

  const cancelPreview = () => router.get("/admin/fleet_deploys");

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8">
      <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Fleet deploys</h1>
      <p className="mt-1 text-sm text-gray-500">
        Batch redeploys across every eligible site. Newest first.
      </p>

      {can_trigger && !preview && (
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (e.g. tracking.ts update)"
            className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
          />
          <button
            onClick={showPreview}
            disabled={!reason.trim()}
            className="rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-40"
          >
            Deploy fleet
          </button>
        </div>
      )}

      {can_trigger && preview && (
        <div className="mt-4 rounded-lg border border-orange-300 bg-orange-50 p-4 dark:border-orange-800 dark:bg-orange-950">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-sm font-semibold text-orange-900 dark:text-orange-200">
                Preview — about to redeploy {preview.count} site{preview.count === 1 ? "" : "s"}
              </h2>
              <p className="mt-0.5 text-xs text-orange-800 dark:text-orange-300">
                Reason: {preview.reason}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={cancelPreview}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeploy}
                disabled={preview.count === 0}
                className="rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-40"
              >
                Confirm deploy ({preview.count})
              </button>
            </div>
          </div>

          {preview.count > 0 ? (
            <ul className="mt-3 max-h-64 overflow-y-auto rounded border border-orange-200 bg-white text-sm dark:border-orange-900 dark:bg-gray-900">
              {preview.sites.map((site) => (
                <li key={site.website_id} className="border-b border-gray-100 px-3 py-1.5 last:border-0 dark:border-gray-800">
                  {site.url ? (
                    <a href={site.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                      {site.url}
                    </a>
                  ) : (
                    <span className="text-gray-400">website #{site.website_id} (no url)</span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-3 text-sm text-orange-800 dark:text-orange-300">No eligible sites.</p>
          )}
        </div>
      )}

      <div className="mt-6 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              {["Reason", "Status", "Sites", "Failed", "Triggered by", "When", ""].map((h) => (
                <th key={h} className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {fleet_deploys.map((fleet) => (
              <tr key={fleet.id}>
                <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                  {fleet.reason}
                  {fleet.parent_id && (
                    <span className="ml-2 text-xs text-gray-400">retry of #{fleet.parent_id}</span>
                  )}
                </td>
                <td className="px-4 py-2"><StatusBadge status={fleet.status} /></td>
                <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">{fleet.total_count}</td>
                <td className="px-4 py-2 text-sm">
                  <span className={fleet.failed_count > 0 ? "font-semibold text-red-600" : "text-gray-400"}>
                    {fleet.failed_count}
                  </span>
                </td>
                <td className="px-4 py-2 text-sm text-gray-500">{fleet.triggered_by_email || "—"}</td>
                <td className="px-4 py-2 text-sm text-gray-500">
                  {fleet.created_at ? new Date(fleet.created_at).toLocaleString() : "—"}
                </td>
                <td className="px-4 py-2 text-right">
                  <Link href={fleet.url} className="text-sm font-medium text-blue-600 hover:underline">
                    View
                  </Link>
                </td>
              </tr>
            ))}
            {fleet_deploys.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                  No fleet deploys yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
        <span>
          Page {pagination.current_page} of {pagination.total_pages} · {pagination.total_count} total
        </span>
        <div className="flex gap-2">
          <button
            disabled={!pagination.prev_page}
            onClick={() => router.get(window.location.pathname, { page: pagination.prev_page })}
            className="rounded border border-gray-300 px-3 py-1 disabled:opacity-40"
          >
            Prev
          </button>
          <button
            disabled={!pagination.next_page}
            onClick={() => router.get(window.location.pathname, { page: pagination.next_page })}
            className="rounded border border-gray-300 px-3 py-1 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

FleetDeploysIndex.layout = (page: React.ReactNode) => createElement(AdminLayout, null, page);
